import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { RosterRef } from '../components/invitations/models/invitation.model';
import { ORGS_SEED } from './data/orgs.seed';

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v));

/**
 * The one mock organization list, shared by invitations and inventory.
 *
 * Ownership used to sit inside InvitationMockService; it moved here when inventory's key
 * transfer needed the same list. Two lists would be a visible bug — the same app showing two
 * different sets of organizations, and a transfer naming a source the other screen denies.
 *
 * In-memory only (as it was before): the org list isn't persisted, so a reload restores the
 * seed. Invitations' scoring-key adjustments ride on it via `adjustKeys`.
 */
@Injectable({ providedIn: 'root' })
export class OrgMockService {
  private orgs: RosterRef[] = clone(ORGS_SEED);
  private counter = 1;

  list(): Observable<RosterRef[]> { return of(clone(this.orgs)); }

  /** Synchronous read for callers that render inside a change-detection pass. */
  listSync(): RosterRef[] { return clone(this.orgs); }

  getById(id: string): RosterRef | undefined { return this.orgs.find(o => o.id === id); }

  add(name: string, availableKeys = 0): Observable<RosterRef> {
    const org: RosterRef = { id: `r-new-${this.counter++}`, name, availableKeys };
    this.orgs = [...this.orgs, org];
    return of(clone(org));
  }

  /**
   * Move an org's scoring-key balance by `delta`, clamped at zero, returning the amount actually
   * applied — a transfer can only move what's there.
   */
  adjustKeys(id: string, delta: number): number {
    const org = this.orgs.find(o => o.id === id);
    if (!org) { return 0; }
    const applied = delta < 0 ? -Math.min(org.availableKeys, -delta) : delta;
    org.availableKeys += applied;
    return applied;
  }

  reset(): void {
    this.orgs = clone(ORGS_SEED);
    this.counter = 1;
  }
}
